import React, { useState, useEffect } from 'react';
import { 
    Image as ImageIcon, HardDrive, RefreshCw, Upload, 
    Trash2, CheckCircle2, AlertTriangle, ShieldCheck, 
    Minimize2, Sparkles, ExternalLink, Copy, Check,
    Folder, Database, ArrowRight, Loader2, Info, Eye
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { useThemeTokens } from '../../hooks/useActiveTheme';

interface MediaRecord {
    id: string;
    recordId: string;
    targetId: string;
    collection: 'SkillAsset' | 'ClinicalSimulator' | 'Skill' | 'StudyModule' | 'AdConfig';
    field: string;
    title: string;
    mimeType: string;
    sizeBytes: number;
    sizeFormatted: string;
    previewUrl: string;
    currentUrl: string;
    isBase64: boolean;
    canCompress: boolean;
    certId?: string;
    slideIdx?: number;
    slideId?: string;
    trackIdx?: number;
    migratedUrl?: string;
    savingsPercent?: number;
}

interface AuditSummary {
    totalRecords: number;
    totalBytes: number;
    totalSizeFormatted: string;
    breakdown: {
        SkillAsset: { count: number; bytes: number };
        ClinicalSimulator: { count: number; bytes: number };
        Skill: { count: number; bytes: number };
        StudyModule: { count: number; bytes: number };
        AdConfig: { count: number; bytes: number };
    };
}

export const MediaAuditManager: React.FC = () => {
    const tokens = useThemeTokens();
    const [summary, setSummary] = useState<AuditSummary | null>(null);
    const [records, setRecords] = useState<MediaRecord[]>([]);
    const [statusInfo, setStatusInfo] = useState<any>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [isTestingWix, setIsTestingWix] = useState(false);
    const [activeActionId, setActiveActionId] = useState<string | null>(null);
    const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

    // Batch progress
    const [isBatchMigrating, setIsBatchMigrating] = useState(false);
    const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; percent: number } | null>(null);

    // Manual Upload State
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadPreview, setUploadPreview] = useState<string | null>(null);
    const [compressOnUpload, setCompressOnUpload] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResultUrl, setUploadResultUrl] = useState<string | null>(null);
    const [copiedUrl, setCopiedUrl] = useState(false);

    // Preview Modal
    const [previewModalImg, setPreviewModalImg] = useState<{ url: string; title: string } | null>(null);

    // Filter collection
    const [collectionFilter, setCollectionFilter] = useState<string>('all');

    // Google Secret Manager Sync State
    const [gcpProjectId, setGcpProjectId] = useState<string>('dictation-champ');
    const [isSyncingSecrets, setIsSyncingSecrets] = useState(false);
    const [showManualConfig, setShowManualConfig] = useState(false);
    const [manualApiKey, setManualApiKey] = useState('');
    const [manualSiteId, setManualSiteId] = useState('');
    const [manualAccountId, setManualAccountId] = useState('');
    const [manualFolderId, setManualFolderId] = useState('bcb0f2276e9c48ceab3b4cc5ebe64e37');

    const checkStatus = async (project?: string) => {
        setIsTestingWix(true);
        try {
            const res = await StorageService.getMediaAuditStatus(project || gcpProjectId);
            setStatusInfo(res);
        } catch (e: any) {
            setStatusInfo({ connected: false, message: e.message });
        } finally {
            setIsTestingWix(false);
        }
    };

    const handleSyncSecrets = async () => {
        setIsSyncingSecrets(true);
        setActionMessage(null);
        try {
            const res = await StorageService.syncSecretManager(gcpProjectId);
            setStatusInfo((prev: any) => ({
                ...prev,
                connected: res.connected,
                message: res.message,
                syncReport: res.syncResult,
                credentials: {
                    hasApiKey: !!res.syncResult?.keys?.WIX_SITE_MEDIA_API_KEY?.fetched,
                    hasSiteId: !!res.syncResult?.keys?.WIX_SITE_ID?.fetched,
                    hasAccountId: !!res.syncResult?.keys?.WIX_ACCOUNT_ID?.fetched,
                    parentFolderId: 'bcb0f2276e9c48ceab3b4cc5ebe64e37'
                }
            }));

            if (res.syncResult?.allFetched) {
                setActionMessage({
                    type: 'success',
                    text: 'All 3 keys (WIX_SITE_MEDIA_API_KEY, WIX_SITE_ID, WIX_ACCOUNT_ID) successfully fetched from Google Secret Manager!'
                });
            } else {
                setActionMessage({
                    type: 'info',
                    text: 'Google Secret Manager checked. Verify project name and IAM permissions below if keys were not accessible.'
                });
            }
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Secret Manager sync failed.' });
        } finally {
            setIsSyncingSecrets(false);
        }
    };

    const handleSaveManualConfig = async () => {
        try {
            const res = await StorageService.configureMediaCredentials({
                apiKey: manualApiKey,
                siteId: manualSiteId,
                accountId: manualAccountId,
                parentFolderId: manualFolderId,
                gcpProjectId: gcpProjectId
            });
            setStatusInfo(res);
            setActionMessage({
                type: 'success',
                text: 'Wix Media credentials updated successfully!'
            });
            setShowManualConfig(false);
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Failed to update configuration.' });
        }
    };

    const runScan = async () => {
        setIsScanning(true);
        setActionMessage(null);
        try {
            const data = await StorageService.scanMediaAudit();
            setSummary(data.summary);
            setRecords(data.records || []);
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Failed to scan MongoDB media assets.' });
        } finally {
            setIsScanning(false);
        }
    };

    useEffect(() => {
        checkStatus();
        runScan();
    }, []);

    // 1. Single Record Migration to Wix
    const handleMigrateOne = async (record: MediaRecord, compress: boolean = false) => {
        setActiveActionId(record.id);
        setActionMessage(null);
        try {
            const res = await StorageService.migrateMediaRecord({
                recordId: record.recordId,
                collection: record.collection,
                field: record.field,
                targetId: record.targetId,
                certId: record.certId,
                slideIdx: record.slideIdx,
                slideId: record.slideId,
                trackIdx: record.trackIdx,
                compress
            });

            // Update local records
            setRecords(prev => prev.map(r => {
                if (r.id === record.id) {
                    return {
                        ...r,
                        currentUrl: res.wixCdnUrl,
                        migratedUrl: res.wixCdnUrl,
                        isBase64: false,
                        savingsPercent: res.savingsPercent
                    };
                }
                return r;
            }));

            setActionMessage({
                type: 'success',
                text: `Successfully migrated "${record.title}" to Wix CDN (${res.wixCdnUrl}). Legacy Base64 purged.`
            });
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Failed to migrate asset.' });
        } finally {
            setActiveActionId(null);
        }
    };

    // 2. Single-click Image Compression and Re-upload
    const handleCompressOne = async (record: MediaRecord) => {
        setActiveActionId(record.id);
        setActionMessage(null);
        try {
            const res = await StorageService.compressMediaRecord({
                recordId: record.recordId,
                collection: record.collection,
                field: record.field,
                targetId: record.targetId,
                certId: record.certId,
                slideIdx: record.slideIdx,
                slideId: record.slideId,
                trackIdx: record.trackIdx,
                currentUrl: record.migratedUrl || record.currentUrl
            });

            setRecords(prev => prev.map(r => {
                if (r.id === record.id) {
                    return {
                        ...r,
                        currentUrl: res.newUrl,
                        migratedUrl: res.newUrl,
                        isBase64: false,
                        savingsPercent: res.savingsPercent,
                        sizeFormatted: `${(res.compressedBytes / 1024).toFixed(1)} KB`
                    };
                }
                return r;
            }));

            setActionMessage({
                type: 'success',
                text: `Compressed "${record.title}" by ${res.savingsPercent}% without quality loss! Re-uploaded to Wix CDN.`
            });
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Failed to compress image.' });
        } finally {
            setActiveActionId(null);
        }
    };

    // 3. Delete / Purge Data Buffer from MongoDB
    const handleDeleteBuffer = async (record: MediaRecord) => {
        if (!confirm(`Are you sure you want to permanently delete the Base64 data buffer for "${record.title}" from MongoDB?`)) {
            return;
        }

        setActiveActionId(record.id);
        setActionMessage(null);
        try {
            await StorageService.deleteMediaBuffer({
                recordId: record.recordId,
                collection: record.collection,
                field: record.field,
                targetId: record.targetId,
                certId: record.certId,
                slideIdx: record.slideIdx,
                slideId: record.slideId,
                trackIdx: record.trackIdx
            });

            setRecords(prev => prev.filter(r => r.id !== record.id));
            if (summary) {
                setSummary({
                    ...summary,
                    totalRecords: Math.max(0, summary.totalRecords - 1),
                    totalBytes: Math.max(0, summary.totalBytes - record.sizeBytes)
                });
            }

            setActionMessage({
                type: 'success',
                text: `Base64 media buffer permanently deleted from MongoDB.`
            });
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Failed to delete data buffer.' });
        } finally {
            setActiveActionId(null);
        }
    };

    // 4. Batch Migrate All
    const handleBatchMigrate = async () => {
        const unmigrated = records.filter(r => r.isBase64);
        if (unmigrated.length === 0) {
            alert('No unmigrated Base64 records found to process.');
            return;
        }

        if (!confirm(`Initiate batch migration of ${unmigrated.length} Base64 assets directly to Wix Media CDN? This will upload them to parent folder "bcb0f2276e9c48ceab3b4cc5ebe64e37", update MongoDB documents, and clean up Base64 strings.`)) {
            return;
        }

        setIsBatchMigrating(true);
        setBatchProgress({ current: 0, total: unmigrated.length, percent: 0 });

        try {
            const res = await StorageService.batchMigrateMedia(unmigrated, true);
            setActionMessage({
                type: 'success',
                text: `Batch migration complete: ${res.migrated} assets successfully moved to Wix CDN (${((res.totalBytesRecovered || 0) / (1024 * 1024)).toFixed(2)} MB database storage recovered).`
            });
            // Refresh scan
            runScan();
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Batch migration failed.' });
        } finally {
            setIsBatchMigrating(false);
            setBatchProgress(null);
        }
    };

    // 5. Manual Upload Handler
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadFile(file);
        setUploadResultUrl(null);

        const reader = new FileReader();
        reader.onload = () => {
            setUploadPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleExecuteManualUpload = async () => {
        if (!uploadFile || !uploadPreview) return;
        setIsUploading(true);
        setActionMessage(null);
        try {
            const res = await StorageService.manualUploadMedia({
                base64Data: uploadPreview,
                fileName: uploadFile.name,
                mimeType: uploadFile.type || 'image/jpeg',
                mediaType: uploadFile.type.startsWith('audio/') ? 'AUDIO' : (uploadFile.type.startsWith('video/') ? 'VIDEO' : 'IMAGE'),
                compress: compressOnUpload
            });

            setUploadResultUrl(res.wixCdnUrl);
            setActionMessage({
                type: 'success',
                text: `Uploaded directly to Wix Media folder (bcb0f2276e9c48ceab3b4cc5ebe64e37). CDN URL generated!`
            });
        } catch (e: any) {
            setActionMessage({ type: 'error', text: e.message || 'Manual upload failed.' });
        } finally {
            setIsUploading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const filteredRecords = collectionFilter === 'all' 
        ? records 
        : records.filter(r => r.collection === collectionFilter);

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Header Banner */}
            <div className={`p-8 rounded-[2.5rem] border ${tokens.heroBannerBg} text-white shadow-2xl relative overflow-hidden`}>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <span className="p-2.5 rounded-2xl bg-white/10 backdrop-blur-md">
                                <ImageIcon size={28} className={tokens.accentText} />
                            </span>
                            <h2 className={`text-2xl font-black tracking-tight ${tokens.headerFont}`}>
                                Wix Media CDN & Database Storage Audit
                            </h2>
                        </div>
                        <p className="text-white/80 text-sm max-w-2xl leading-relaxed">
                            Audit, migrate, and clean up <code className="px-2 py-0.5 rounded bg-white/10 font-mono text-xs">data:image</code> Base64 blobs stored in MongoDB. Uploads directly to Wix Media Manager in parent folder <code className="px-2 py-0.5 rounded bg-white/10 font-mono text-xs">bcb0f2276e9c48ceab3b4cc5ebe64e37</code> via Option B REST API, replaces embedded data with CDN URLs, and purges database bloat.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={runScan}
                            disabled={isScanning}
                            className={`px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${tokens.secondaryBtn} active:scale-95 disabled:opacity-50`}
                        >
                            <RefreshCw size={16} className={isScanning ? 'animate-spin' : ''} />
                            {isScanning ? 'Scanning...' : 'Scan Database'}
                        </button>

                        <button
                            onClick={() => setShowUploadModal(true)}
                            className={`px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg transition-all ${tokens.primaryBtn} active:scale-95`}
                        >
                            <Upload size={16} />
                            Manual Upload to Wix
                        </button>
                    </div>
                </div>
            </div>

            {/* Notification Alert */}
            {actionMessage && (
                <div className={`p-4 rounded-2xl border flex items-center justify-between gap-4 animate-pop ${
                    actionMessage.type === 'success' 
                        ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300'
                        : actionMessage.type === 'error'
                        ? 'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300'
                        : 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800 text-indigo-800 dark:text-indigo-300'
                }`}>
                    <div className="flex items-center gap-3">
                        {actionMessage.type === 'success' ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                        <span className="text-xs font-bold leading-relaxed">{actionMessage.text}</span>
                    </div>
                    <button onClick={() => setActionMessage(null)} className="text-xs opacity-60 hover:opacity-100 font-bold">
                        Dismiss
                    </button>
                </div>
            )}

            {/* Google Secret Manager Connection & Sync Card */}
            <div className={`p-6 rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-lg space-y-5`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-3.5 h-3.5 rounded-full ${statusInfo?.connected ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                                    Google Cloud Secret Manager & Wix REST API
                                </h4>
                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                    statusInfo?.connected 
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' 
                                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                }`}>
                                    {statusInfo?.connected ? 'Wix API Authenticated' : 'Awaiting Secret Verification'}
                                </span>
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                {statusInfo?.message || 'Ready to fetch secrets from Google Cloud Secret Manager.'}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200 dark:border-slate-700">
                            <span className="text-[10px] font-black text-slate-400 uppercase px-2">Project:</span>
                            <input
                                type="text"
                                value={gcpProjectId}
                                onChange={e => setGcpProjectId(e.target.value)}
                                placeholder="GCP Project ID (e.g. dictation-champ)"
                                className="bg-white dark:bg-slate-900 px-3 py-1.5 rounded-xl text-xs font-mono font-bold text-slate-800 dark:text-slate-200 border-none outline-none w-44"
                            />
                        </div>

                        <button
                            onClick={handleSyncSecrets}
                            disabled={isSyncingSecrets}
                            className={`px-4 py-2 rounded-2xl font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${tokens.primaryBtn} active:scale-95 shadow-md disabled:opacity-50`}
                            title="Query Google Secret Manager for WIX_SITE_MEDIA_API_KEY, WIX_SITE_ID, and WIX_ACCOUNT_ID"
                        >
                            {isSyncingSecrets ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                            {isSyncingSecrets ? 'Syncing...' : 'Sync From Secret Manager'}
                        </button>

                        <button
                            onClick={() => setShowManualConfig(!showManualConfig)}
                            className={`px-3 py-2 rounded-2xl font-bold text-xs border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300`}
                        >
                            {showManualConfig ? 'Hide Config' : 'Manual Config'}
                        </button>
                    </div>
                </div>

                {/* Secret Status Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 text-[11px]">
                    {/* WIX_SITE_MEDIA_API_KEY */}
                    <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800">
                        <div className="flex items-center justify-between text-slate-400 mb-1">
                            <span className="font-bold">WIX_SITE_MEDIA_API_KEY</span>
                            {statusInfo?.credentials?.hasApiKey ? (
                                <CheckCircle2 size={14} className="text-emerald-500" />
                            ) : (
                                <AlertTriangle size={14} className="text-amber-500" />
                            )}
                        </div>
                        <div className="font-mono font-bold text-slate-800 dark:text-slate-200 truncate">
                            {statusInfo?.credentials?.hasApiKey ? 'Loaded from GCP Secret' : 'Not Loaded'}
                        </div>
                    </div>

                    {/* WIX_SITE_ID */}
                    <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800">
                        <div className="flex items-center justify-between text-slate-400 mb-1">
                            <span className="font-bold">WIX_SITE_ID</span>
                            {statusInfo?.credentials?.hasSiteId ? (
                                <CheckCircle2 size={14} className="text-emerald-500" />
                            ) : (
                                <AlertTriangle size={14} className="text-amber-500" />
                            )}
                        </div>
                        <div className="font-mono font-bold text-slate-800 dark:text-slate-200 truncate">
                            {statusInfo?.credentials?.hasSiteId ? 'Loaded from GCP Secret' : 'Not Loaded'}
                        </div>
                    </div>

                    {/* WIX_ACCOUNT_ID */}
                    <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800">
                        <div className="flex items-center justify-between text-slate-400 mb-1">
                            <span className="font-bold">WIX_ACCOUNT_ID</span>
                            {statusInfo?.credentials?.hasAccountId ? (
                                <CheckCircle2 size={14} className="text-emerald-500" />
                            ) : (
                                <Info size={14} className="text-slate-400" />
                            )}
                        </div>
                        <div className="font-mono font-bold text-slate-800 dark:text-slate-200 truncate">
                            {statusInfo?.credentials?.hasAccountId ? 'Loaded from GCP Secret' : 'Optional / Configured'}
                        </div>
                    </div>

                    {/* Target Folder */}
                    <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800">
                        <div className="flex items-center justify-between text-slate-400 mb-1">
                            <span className="font-bold">Parent Folder ID</span>
                            <Folder size={14} className="text-emerald-500" />
                        </div>
                        <div className="font-mono font-bold text-emerald-600 dark:text-emerald-400 truncate text-[10px]" title="bcb0f2276e9c48ceab3b4cc5ebe64e37">
                            bcb0f2276e9c48ceab3b4cc5ebe64e37
                        </div>
                    </div>
                </div>

                {/* IAM Instructions Note if not connected */}
                {!statusInfo?.connected && (
                    <div className="p-4 rounded-2xl bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200 text-xs space-y-2">
                        <div className="flex items-center gap-2 font-black">
                            <ShieldCheck size={16} className="text-amber-600 dark:text-amber-400" />
                            <span>Google Cloud Secret Manager Access Requirements</span>
                        </div>
                        <p className="text-[11px] leading-relaxed text-amber-800 dark:text-amber-300">
                            Ensure secrets <code className="font-mono font-bold px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/60 rounded">WIX_SITE_MEDIA_API_KEY</code>, <code className="font-mono font-bold px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/60 rounded">WIX_SITE_ID</code>, and <code className="font-mono font-bold px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/60 rounded">WIX_ACCOUNT_ID</code> exist in Google Secret Manager. To allow automatic reading from this environment, grant the Service Account below the role: <strong>Secret Manager Secret Accessor</strong> (<code className="font-mono text-[10px]">roles/secretmanager.secretAccessor</code>):
                        </p>
                        <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-amber-100/60 dark:bg-amber-900/50 font-mono text-[10px] select-all">
                            <span>ais-sandbox@ais-us-east1-b82de026803a41d19.iam.gserviceaccount.com</span>
                            <button
                                onClick={() => copyToClipboard('ais-sandbox@ais-us-east1-b82de026803a41d19.iam.gserviceaccount.com')}
                                className="font-sans font-bold text-amber-800 dark:text-amber-300 hover:underline px-2 py-0.5"
                            >
                                {copiedUrl ? 'Copied' : 'Copy Email'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Collapsible Manual Config Form */}
                {showManualConfig && (
                    <div className="p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-4 animate-pop">
                        <div className="flex items-center justify-between">
                            <h5 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                                Runtime Credentials Configuration / Fallback
                            </h5>
                            <span className="text-[10px] text-slate-400">Updates runtime credentials and verifies connection</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">WIX_SITE_MEDIA_API_KEY</label>
                                <input
                                    type="password"
                                    value={manualApiKey}
                                    onChange={e => setManualApiKey(e.target.value)}
                                    placeholder="Paste Wix Site Media API Key"
                                    className="w-full text-xs font-mono p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">WIX_SITE_ID</label>
                                <input
                                    type="text"
                                    value={manualSiteId}
                                    onChange={e => setManualSiteId(e.target.value)}
                                    placeholder="Paste Wix Site ID"
                                    className="w-full text-xs font-mono p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block mb-1">WIX_ACCOUNT_ID</label>
                                <input
                                    type="text"
                                    value={manualAccountId}
                                    onChange={e => setManualAccountId(e.target.value)}
                                    placeholder="Paste Wix Account ID"
                                    className="w-full text-xs font-mono p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                onClick={() => setShowManualConfig(false)}
                                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveManualConfig}
                                className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider ${tokens.primaryBtn} shadow-md`}
                            >
                                Save & Test Connection
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Metrics Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className={`p-6 rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-sm space-y-2`}>
                    <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs font-black uppercase tracking-wider">Base64 Images</span>
                        <HardDrive size={18} className={tokens.accentText} />
                    </div>
                    <div className="text-3xl font-black text-slate-900 dark:text-white">
                        {summary?.totalRecords ?? 0}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Total embedded data images stored in MongoDB
                    </p>
                </div>

                <div className={`p-6 rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-sm space-y-2`}>
                    <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs font-black uppercase tracking-wider">Database Weight</span>
                        <Database size={18} className="text-amber-500" />
                    </div>
                    <div className="text-3xl font-black text-amber-600 dark:text-amber-400">
                        {summary?.totalSizeFormatted ?? '0 B'}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Memory & disk consumed by Base64 buffers
                    </p>
                </div>

                <div className={`p-6 rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-sm space-y-2`}>
                    <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs font-black uppercase tracking-wider">Skill Slide Assets</span>
                        <Sparkles size={18} className="text-indigo-500" />
                    </div>
                    <div className="text-3xl font-black text-indigo-600 dark:text-indigo-400">
                        {summary?.breakdown?.SkillAsset?.count ?? 0}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Legacy SkillAsset documents pending migration
                    </p>
                </div>

                <div className={`p-6 rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-sm space-y-2`}>
                    <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs font-black uppercase tracking-wider">360 Simulators</span>
                        <ImageIcon size={18} className="text-emerald-500" />
                    </div>
                    <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400">
                        {summary?.breakdown?.ClinicalSimulator?.count ?? 0}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        AI-synthesized custom panoramas in DB
                    </p>
                </div>
            </div>

            {/* Action Bar & Collection Filters */}
            <div className={`p-6 rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-md flex flex-col md:flex-row md:items-center justify-between gap-4`}>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-black uppercase tracking-wider text-slate-400 mr-2">Filter:</span>
                    {[
                        { id: 'all', label: 'All Collections' },
                        { id: 'SkillAsset', label: `SkillAsset (${summary?.breakdown?.SkillAsset?.count || 0})` },
                        { id: 'ClinicalSimulator', label: `ClinicalSim (${summary?.breakdown?.ClinicalSimulator?.count || 0})` },
                        { id: 'Skill', label: `Skills (${summary?.breakdown?.Skill?.count || 0})` },
                        { id: 'StudyModule', label: `Modules (${summary?.breakdown?.StudyModule?.count || 0})` },
                        { id: 'AdConfig', label: `Ads (${summary?.breakdown?.AdConfig?.count || 0})` }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setCollectionFilter(tab.id)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                collectionFilter === tab.id ? tokens.activeTab : tokens.inactiveTab
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBatchMigrate}
                        disabled={isBatchMigrating || records.filter(r => r.isBase64).length === 0}
                        className={`px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${tokens.primaryBtn} shadow-md active:scale-95 disabled:opacity-50`}
                    >
                        {isBatchMigrating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                        {isBatchMigrating ? 'Migrating to Wix...' : 'Batch Migrate All to Wix'}
                    </button>
                </div>
            </div>

            {/* Batch Migration Progress Indicator */}
            {isBatchMigrating && batchProgress && (
                <div className={`p-6 rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-lg space-y-3 animate-pop`}>
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                        <span>Migrating images to Wix Media CDN (folder: bcb0f2276e9c48ceab3b4cc5ebe64e37)...</span>
                        <span>{batchProgress.current} / {batchProgress.total}</span>
                    </div>
                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div 
                            className={`h-full ${tokens.progressBar} transition-all duration-300`}
                            style={{ width: `${Math.max(10, (batchProgress.current / batchProgress.total) * 100)}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Records Table / List */}
            <div className={`rounded-3xl border ${tokens.cardBg} ${tokens.accentBorder} shadow-lg overflow-hidden`}>
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <div>
                        <h3 className={`text-base font-black tracking-tight text-slate-900 dark:text-white ${tokens.headerFont}`}>
                            Detected Base64 Media Assets ({filteredRecords.length})
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Use single-click actions to compress, upload to Wix Media CDN, or purge legacy data buffers.
                        </p>
                    </div>
                </div>

                {filteredRecords.length === 0 ? (
                    <div className="p-16 text-center space-y-3">
                        <CheckCircle2 size={48} className="mx-auto text-emerald-500 opacity-80" />
                        <h4 className="text-base font-black text-slate-800 dark:text-slate-200">
                            No unmanaged Base64 media found!
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                            All media assets in the selected scope are cleanly hosted on Wix Media CDN or external endpoints. MongoDB storage is lean.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] font-black uppercase tracking-wider text-slate-400 bg-slate-50/50 dark:bg-slate-900/50">
                                    <th className="p-4 pl-6">Preview</th>
                                    <th className="p-4">Asset Details & Target</th>
                                    <th className="p-4">Collection / Field</th>
                                    <th className="p-4">Size</th>
                                    <th className="p-4">Hosting Status</th>
                                    <th className="p-4 pr-6 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                                {filteredRecords.map(record => {
                                    const isActing = activeActionId === record.id;
                                    const isMigrated = !record.isBase64 || !!record.migratedUrl;

                                    return (
                                        <tr key={record.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                                            {/* Preview Column */}
                                            <td className="p-4 pl-6">
                                                <div 
                                                    onClick={() => setPreviewModalImg({ 
                                                        url: record.migratedUrl || record.previewUrl || record.currentUrl, 
                                                        title: record.title 
                                                    })}
                                                    className="w-16 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden relative cursor-pointer group flex items-center justify-center shrink-0"
                                                    title="Click to view full preview"
                                                >
                                                    {record.previewUrl ? (
                                                        <img 
                                                            src={record.migratedUrl || record.previewUrl} 
                                                            alt={record.title}
                                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                        />
                                                    ) : (
                                                        <ImageIcon size={20} className="text-slate-400" />
                                                    )}
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                                                        <Eye size={14} />
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Details Column */}
                                            <td className="p-4 max-w-xs">
                                                <div className="font-bold text-slate-800 dark:text-slate-100 line-clamp-1">
                                                    {record.title}
                                                </div>
                                                <div className="text-[10px] text-slate-400 font-mono mt-0.5 truncate" title={record.targetId}>
                                                    ID: {record.targetId}
                                                </div>
                                            </td>

                                            {/* Collection Column */}
                                            <td className="p-4">
                                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${
                                                    record.collection === 'SkillAsset'
                                                        ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800'
                                                        : record.collection === 'ClinicalSimulator'
                                                        ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                                }`}>
                                                    {record.collection}
                                                </span>
                                                <div className="text-[10px] text-slate-400 font-mono mt-1">
                                                    {record.field}
                                                </div>
                                            </td>

                                            {/* Size Column */}
                                            <td className="p-4 font-mono font-bold text-slate-700 dark:text-slate-300">
                                                {record.sizeFormatted}
                                                {record.savingsPercent ? (
                                                    <span className="block text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                                                        -{record.savingsPercent}% saved
                                                    </span>
                                                ) : null}
                                            </td>

                                            {/* Status Column */}
                                            <td className="p-4">
                                                {isMigrated ? (
                                                    <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-bold text-[11px]">
                                                        <CheckCircle2 size={14} />
                                                        <span>Wix CDN Hosted</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-bold text-[11px]">
                                                        <AlertTriangle size={14} />
                                                        <span>Base64 in MongoDB</span>
                                                    </div>
                                                )}
                                                {record.migratedUrl && (
                                                    <a 
                                                        href={record.migratedUrl} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-[10px] font-mono text-indigo-500 hover:underline flex items-center gap-1 mt-0.5"
                                                    >
                                                        View CDN <ExternalLink size={10} />
                                                    </a>
                                                )}
                                            </td>

                                            {/* Action Buttons */}
                                            <td className="p-4 pr-6 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {/* 1. Migrate to Wix Button */}
                                                    <button
                                                        onClick={() => handleMigrateOne(record, false)}
                                                        disabled={isActing}
                                                        className={`px-3 py-1.5 rounded-xl font-bold text-[11px] flex items-center gap-1.5 transition-all ${
                                                            isMigrated 
                                                                ? 'bg-slate-100 text-slate-400 dark:bg-slate-800' 
                                                                : `${tokens.primaryBtn} shadow-sm active:scale-95`
                                                        } disabled:opacity-50`}
                                                        title="Upload Base64 to Wix Media in folder bcb0f2276e9c48ceab3b4cc5ebe64e37 and replace in DB"
                                                    >
                                                        {isActing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                                        {isMigrated ? 'Re-Migrate' : 'Migrate to Wix'}
                                                    </button>

                                                    {/* 2. Single-click Compress & Re-upload Button */}
                                                    <button
                                                        onClick={() => handleCompressOne(record)}
                                                        disabled={isActing}
                                                        className={`px-3 py-1.5 rounded-xl font-bold text-[11px] flex items-center gap-1.5 transition-all ${tokens.secondaryBtn} active:scale-95 disabled:opacity-50`}
                                                        title="Smart lossless/visually lossless compression with Sharp and re-upload to Wix"
                                                    >
                                                        <Minimize2 size={12} className="text-amber-500" />
                                                        Compress
                                                    </button>

                                                    {/* 3. Purge / Delete Buffer Button */}
                                                    <button
                                                        onClick={() => handleDeleteBuffer(record)}
                                                        disabled={isActing}
                                                        className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-all active:scale-95 disabled:opacity-50"
                                                        title="Delete Base64 data buffer from MongoDB"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Manual Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                    <div className={`w-full max-w-lg rounded-[2.5rem] border ${tokens.cardBg} ${tokens.accentBorder} shadow-2xl overflow-hidden p-8 space-y-6 animate-pop`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="p-2.5 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600">
                                    <Upload size={20} />
                                </span>
                                <div>
                                    <h3 className={`text-lg font-black text-slate-900 dark:text-white ${tokens.headerFont}`}>
                                        Manual Upload to Wix Media
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Uploads directly to folder <code className="font-mono text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">bcb0f2276e9c48ceab3b4cc5ebe64e37</code>
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setShowUploadModal(false); setUploadFile(null); setUploadPreview(null); setUploadResultUrl(null); }}
                                className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400"
                            >
                                ✕
                            </button>
                        </div>

                        {/* File Selector */}
                        <div className="space-y-4">
                            <label className="block p-6 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-indigo-500 text-center cursor-pointer transition-colors bg-slate-50/50 dark:bg-slate-900/50">
                                <input
                                    type="file"
                                    accept="image/*,audio/*,video/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                {uploadPreview ? (
                                    <div className="space-y-3">
                                        {uploadFile?.type.startsWith('image/') ? (
                                            <img src={uploadPreview} alt="Preview" className="max-h-48 mx-auto rounded-xl object-contain shadow-md" />
                                        ) : (
                                            <div className="p-6 bg-slate-200 dark:bg-slate-800 rounded-xl font-mono text-xs">
                                                {uploadFile?.name} ({((uploadFile?.size || 0) / 1024).toFixed(1)} KB)
                                            </div>
                                        )}
                                        <p className="text-xs text-indigo-500 font-bold">Click to choose another file</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 py-4">
                                        <Upload size={32} className="mx-auto text-slate-400" />
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                            Click or drag media file to upload
                                        </p>
                                        <p className="text-[10px] text-slate-400">
                                            Supports JPEG, PNG, WebP, MP3 audio, and MP4 video clips
                                        </p>
                                    </div>
                                )}
                            </label>

                            {/* Single Click Compression Checkbox */}
                            <label className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={compressOnUpload}
                                    onChange={e => setCompressOnUpload(e.target.checked)}
                                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                                />
                                <div>
                                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">
                                        Optimize image size (Quality Preserving Compression)
                                    </span>
                                    <span className="text-[10px] text-slate-400 block">
                                        Uses Sharp MozJPEG/WebP recompression to trim 50-70% file weight before upload
                                    </span>
                                </div>
                            </label>

                            {/* Result URL display */}
                            {uploadResultUrl && (
                                <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 space-y-2 animate-fadeIn">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                                            Permanent Wix CDN URL
                                        </span>
                                        <button
                                            onClick={() => copyToClipboard(uploadResultUrl)}
                                            className="text-xs font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1 hover:underline"
                                        >
                                            {copiedUrl ? <Check size={12} /> : <Copy size={12} />}
                                            {copiedUrl ? 'Copied!' : 'Copy URL'}
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        readOnly
                                        value={uploadResultUrl}
                                        className="w-full text-xs font-mono p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-700 text-slate-800 dark:text-slate-200 select-all"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <button
                                onClick={() => { setShowUploadModal(false); setUploadFile(null); setUploadPreview(null); setUploadResultUrl(null); }}
                                className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleExecuteManualUpload}
                                disabled={!uploadFile || isUploading}
                                className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 ${tokens.primaryBtn} shadow-lg active:scale-95 disabled:opacity-50`}
                            >
                                {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                {isUploading ? 'Uploading to Wix...' : 'Upload to Wix CDN'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full Image Preview Modal */}
            {previewModalImg && (
                <div 
                    onClick={() => setPreviewModalImg(null)}
                    className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-fadeIn cursor-pointer"
                >
                    <div 
                        onClick={e => e.stopPropagation()}
                        className="max-w-4xl max-h-[90vh] bg-slate-900 rounded-3xl p-4 overflow-hidden border border-slate-700 shadow-2xl space-y-3 cursor-default"
                    >
                        <div className="flex items-center justify-between text-white text-xs px-2 font-bold">
                            <span className="truncate">{previewModalImg.title}</span>
                            <button onClick={() => setPreviewModalImg(null)} className="p-1 hover:bg-white/10 rounded-lg">✕</button>
                        </div>
                        <img 
                            src={previewModalImg.url} 
                            alt={previewModalImg.title} 
                            className="max-h-[75vh] w-auto mx-auto rounded-2xl object-contain"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
